import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { organization, user } from '../auth'
import { ShareEntityTypeEnum } from '../enums'

export const ContentShare = pgTable(
  'content_share',
  {
    id: uuid().primaryKey().defaultRandom(),
    actorId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    // Nullable: parent-to-child sharing is not organization-scoped.
    organizationId: text().references(() => organization.id, { onDelete: 'cascade' }),
    entityType: ShareEntityTypeEnum().notNull(),
    entityId: text().notNull(),
    note: text(),
    revokedAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (table) => [
    index('idx_content_share_actor').on(table.actorId),
    index('idx_content_share_organization').on(table.organizationId),
    index('idx_content_share_entity').on(table.entityType, table.entityId),
  ],
)

export const ContentShareRecipient = pgTable(
  'content_share_recipient',
  {
    id: uuid().primaryKey().defaultRandom(),
    shareId: uuid()
      .notNull()
      .references(() => ContentShare.id, { onDelete: 'cascade' }),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    revokedAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    index('idx_content_share_recipient_share').on(table.shareId),
    index('idx_content_share_recipient_user').on(table.userId),
    unique('uq_content_share_recipient').on(table.shareId, table.userId),
  ],
)

export const contentShareRelations = relations(ContentShare, ({ one, many }) => ({
  actor: one(user, { fields: [ContentShare.actorId], references: [user.id] }),
  organization: one(organization, { fields: [ContentShare.organizationId], references: [organization.id] }),
  recipients: many(ContentShareRecipient),
}))

export const contentShareRecipientRelations = relations(ContentShareRecipient, ({ one }) => ({
  share: one(ContentShare, { fields: [ContentShareRecipient.shareId], references: [ContentShare.id] }),
  user: one(user, { fields: [ContentShareRecipient.userId], references: [user.id] }),
}))

export const InsertContentShareSchema = createInsertSchema(ContentShare).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateContentShareSchema = createUpdateSchema(ContentShare).partial().omit({
  id: true,
  actorId: true,
  entityType: true,
  entityId: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectContentShareSchema = createSelectSchema(ContentShare)

export const InsertContentShareRecipientSchema = createInsertSchema(ContentShareRecipient).omit({
  id: true,
  createdAt: true,
})

export const SelectContentShareRecipientSchema = createSelectSchema(ContentShareRecipient)

export type InsertContentShare = typeof ContentShare.$inferInsert
export type UpdateContentShare = z.infer<typeof UpdateContentShareSchema>
export type SelectContentShare = typeof ContentShare.$inferSelect
export type InsertContentShareRecipient = typeof ContentShareRecipient.$inferInsert
export type SelectContentShareRecipient = typeof ContentShareRecipient.$inferSelect
